// src/tools/flows.ts

import type { ToolResult } from "../types.js";
import { err, okJson } from "../types.js";
import { findAiAssetsRoot } from "../utils/ai-assets.js";
import { readAllSessions } from "./sessions.js";

// ── Flow tools ────────────────────────────────────────────────────────────────

/**
 * List all flow definitions available in the project's `ai-assets` directory.
 *
 * Returns a JSON array of summary objects `{ id, name, nodeCount }`.
 * Returns an empty array when no `ai-assets` root is found.
 *
 * @param projectDir - Absolute path to the skaile project root.
 *
 * @docLink packages/workspace-plugin/concepts#tool-delivery
 */
export async function flowList(projectDir: string): Promise<ToolResult> {
  try {
    const assetsRoot = await findAiAssetsRoot(projectDir);
    if (!assetsRoot) return okJson([]);
    const { loadAllFlows } = await import(
      "@skaile/workspaces/factory-assets/connectors/flow/engine"
    );
    const flows = loadAllFlows(assetsRoot);
    const summary = flows.map((f: any) => ({
      id: f.id,
      name: f.name ?? f.id,
      nodeCount: f.nodes?.length ?? 0,
    }));
    return okJson(summary);
  } catch (e: unknown) {
    return err(e instanceof Error ? e.message : String(e));
  }
}

/**
 * Show the full flow definition (nodes, edges, metadata) for a specific flow.
 *
 * @param projectDir - Absolute path to the skaile project root.
 * @param flowId     - Flow ID as returned by {@link flowList}.
 *
 * @docLink packages/workspace-plugin/concepts#tool-delivery
 */
export async function flowShow(projectDir: string, flowId: string): Promise<ToolResult> {
  try {
    const assetsRoot = await findAiAssetsRoot(projectDir);
    if (!assetsRoot) return err(`Flow not found: ${flowId}`);
    const { loadAllFlows } = await import(
      "@skaile/workspaces/factory-assets/connectors/flow/engine"
    );
    const flows = loadAllFlows(assetsRoot);
    const flow = flows.find((f: any) => f.id === flowId);
    if (!flow) return err(`Flow not found: ${flowId}`);
    return okJson(flow);
  } catch (e: unknown) {
    return err(e instanceof Error ? e.message : String(e));
  }
}

/**
 * List all past and active flow run sessions for the workspace.
 *
 * Delegates to {@link readAllSessions} (reads `.skaile/sessions/*.json`),
 * sorted newest-first by `updatedAt`.
 *
 * @param projectDir - Absolute path to the skaile project root.
 *
 * @docLink packages/workspace-plugin/concepts#tool-delivery
 */
export async function flowListSessions(projectDir: string): Promise<ToolResult> {
  try {
    const sessions = await readAllSessions(projectDir);
    return okJson(sessions);
  } catch (e: unknown) {
    return err(e instanceof Error ? e.message : String(e));
  }
}

/**
 * Get driver metadata for a specific flow run session.
 *
 * @param projectDir - Absolute path to the skaile project root.
 * @param runId      - Run ID from {@link flowListSessions}.
 *
 * @docLink packages/workspace-plugin/concepts#tool-delivery
 */
export async function flowGetSession(projectDir: string, runId: string): Promise<ToolResult> {
  try {
    const sessions = await readAllSessions(projectDir);
    const session = sessions.find((s) => s.runId === runId);
    if (!session) return err(`Session not found: ${runId}`);
    return okJson(session);
  } catch (e: unknown) {
    return err(e instanceof Error ? e.message : String(e));
  }
}

/**
 * Resolve and return the flow definition that was used in a past session.
 *
 * Looks up the session by `runId`, reads its `flowId`, then returns the full
 * flow definition from `ai-assets`.
 *
 * @param projectDir - Absolute path to the skaile project root.
 * @param runId      - Run ID from {@link flowListSessions}.
 *
 * @docLink packages/workspace-plugin/concepts#tool-delivery
 */
export async function flowFindFlowForSession(
  projectDir: string,
  runId: string,
): Promise<ToolResult> {
  try {
    const sessions = await readAllSessions(projectDir);
    const session = sessions.find((s) => s.runId === runId);
    if (!session) return err(`Session not found: ${runId}`);

    const assetsRoot = await findAiAssetsRoot(projectDir);
    if (!assetsRoot) return err(`Flow not found for session: ${runId} (no ai-assets root)`);

    const { loadAllFlows } = await import(
      "@skaile/workspaces/factory-assets/connectors/flow/engine"
    );
    const flows = loadAllFlows(assetsRoot);
    const flow = flows.find((f: any) => f.id === session.flowId);
    if (!flow) return err(`Flow not found: ${session.flowId}`);
    return okJson(flow);
  } catch (e: unknown) {
    return err(e instanceof Error ? e.message : String(e));
  }
}
